// Import the pure types modules (not the package barrels) so this stays
// importable from Vitest without dragging the chat runtime in.
import { getQuestionFraming, isAskUserQuestionStagedAction, parseAskUserQuestionInput } from './ask-user-question/types'
import {
  feedbackQuestionText,
  getFeedbackFraming,
  isAskUserFeedbackStagedAction,
  parseAskUserFeedbackInput,
} from './ask-user-feedback/types'
import type { AskUserQuestionAnswer } from './ask-user-question/types'
import type { AskUserFeedbackAnswer } from './ask-user-feedback/types'
import type { AskUserCardVariant } from './ask-user-card/theme'
import type { AskUserOptionsGroup } from './ask-user-card/options-preview'

/**
 * A locally-recorded partial answer for either answerable tool, and the payload
 * shape both resolve through. Lives beside the predicates rather than in a view
 * so shared chrome can name it without importing a view.
 */
export type AnswerableProgress = AskUserQuestionAnswer | AskUserFeedbackAnswer

export const ANSWERABLE_PENDING_CONTENT = 'Highlight needs your input'

/**
 * Staged actions the user resolves **by answering them**, rather than by
 * invoking a hydrator: background `AskUserQuestion` and `AskUserFeedback`.
 *
 * The distinction is not cosmetic. Every other staged action is a draft against
 * an integration, and its primary button posts to `/invoke`. These two have no
 * hydrator behind their synthetic toolset, so invoking one is a guaranteed
 * failure — the row has to open the card instead. Any surface with an invoke
 * path must branch on this, not on the question guard alone.
 */
export function isAnswerableStagedAction(action: { toolSet?: string; toolName?: string }): boolean {
  return isAskUserQuestionStagedAction(action) || isAskUserFeedbackStagedAction(action)
}

/**
 * An answerable action still waiting on the user, with no answer recorded and
 * not archived. Every "needs your input" surface (floating bars, glows, thread
 * cards) keys off this one predicate so they agree on who is pending.
 */
export function isPendingAnswerableStagedAction(action: {
  toolSet?: string
  toolName?: string
  outputData?: unknown
  archivedAt?: string | null
}): boolean {
  return isAnswerableStagedAction(action) && action.outputData == null && !action.archivedAt
}

/**
 * The framing sentence for an answerable action — *why* it exists and what
 * answering does, never the question itself. Dispatches to whichever tool this
 * is, because the two say deliberately opposite things: a question names the
 * run it paused, while feedback leads with nothing being paused at all.
 */
export function getAnswerableFraming(action: {
  toolSet?: string
  toolName?: string
  references?: Array<{ title?: string }> | null
  outputData?: unknown
}): string {
  return isAskUserFeedbackStagedAction(action) ? getFeedbackFraming(action) : getQuestionFraming(action)
}

/**
 * Which ask-user card an answerable action opens, and so which tint the rows
 * pointing at it wear. A surface holding only the staged action asks here
 * instead of assuming `input`.
 */
export function askUserCardVariant(action: { toolSet?: string; toolName?: string }): AskUserCardVariant {
  return isAskUserFeedbackStagedAction(action) ? 'feedback' : 'input'
}

/**
 * The choices an answerable action offers, flattened for the read-only chip
 * preview both asks share. Null when there is nothing to show — a caller wraps
 * these in its own bordered box, and a box drawn around an unparseable input is
 * an empty rectangle.
 *
 * Dispatching here rather than at each preview surface is what keeps the two
 * asks looking alike: the surfaces used to reach straight for the question
 * parser, so a feedback card previewed as a bare framing sentence with no chips
 * under it.
 *
 * `hideSingleQuestionPrompt` applies to questions only. A feedback host titles
 * itself with `about` — what the feedback concerns, never the question — so
 * suppressing its prompt would leave chips with nothing to answer.
 */
export function answerableOptionGroups(
  action: { toolSet?: string; toolName?: string; inputData?: unknown },
  { hideSingleQuestionPrompt = false }: { hideSingleQuestionPrompt?: boolean } = {},
): { groups: Array<AskUserOptionsGroup>; showPrompts: boolean } | null {
  if (isAskUserFeedbackStagedAction(action)) {
    const input = parseAskUserFeedbackInput(action.inputData)
    if (!input) return null
    return {
      groups: input.blanks.map((blank) => ({
        header: blank.header,
        prompt: feedbackQuestionText(blank),
        // A blank with no suggestions is free-text-only, and the dashed
        // "Other…" chip the preview always appends is the whole truth about it.
        options: blank.suggestions ?? [],
      })),
      showPrompts: true,
    }
  }
  if (isAskUserQuestionStagedAction(action)) {
    const input = parseAskUserQuestionInput(action.inputData)
    if (!input) return null
    return {
      groups: input.questions.map((question) => ({
        header: question.header,
        prompt: question.question,
        options: question.options.map((option) => option.label),
      })),
      showPrompts: input.questions.length > 1 || !hideSingleQuestionPrompt,
    }
  }
  return null
}
